import React, { useState, useEffect, useRef } from "react";
import {
  ArrowLeft,
  Receipt,
  BarChart2,
  DollarSign,
  Layers,
  FileText,
  CheckSquare,
  Square,
  Download,
  Plus,
  Edit2,
  Trash2,
  MapPin,
  Calendar,
  Clock,
  User,
  Image as ImageIcon,
  X,
  Maximize2,
  AlertCircle,
} from "lucide-react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import axios from "axios";
import { MyDomain } from "../../../App";
import BossCreateExpenses from "./BossCreateExpenses";
import BossUpdateExpensesModal from "./BossUpdateExpensesModal";
import BossModalShowInvoice from "./BossModalShowInvoice";
import BossStatisticByDateModalShowInvoices from "./BossStatisticByDateModalShowInvoices";
import { useTranslation } from "react-i18next";

const getExpenseTypes = (t) => [
  { id: 1, name: t("materials"), icon: "🔨", color: "#3B82F6", bg: "#EFF6FF" },
  { id: 2, name: t("equipment"), icon: "🔧", color: "#8B5CF6", bg: "#F5F3FF" },
  {
    id: 3,
    name: t("transportation"),
    icon: "🚗",
    color: "#10B981",
    bg: "#D1FAE5",
  },
  { id: 4, name: t("utilities"), icon: "⚡", color: "#F59E0B", bg: "#FEF3C7" },
  { id: 5, name: t("otherExpense"), icon: "•••", color: "#6B7280", bg: "#F3F4F6" },
];

export default function BossStatisticCreateExpenses() {
  const { t } = useTranslation();
  const expenseTypes = getExpenseTypes(t);
  const history = useHistory();

  const [allData, setAllData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [choosedMonth, setChoosedMonth] = useState(null);
  const [loading, setLoading] = useState(true);

  const [expenses, setExpenses] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState("all");
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedExpenses, setSelectedExpenses] = useState([]);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showAllInvoicesModal, setShowAllInvoicesModal] = useState(false);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedExpenseImage, setSelectedExpenseImage] = useState(null);

  // Export menu
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Load from localStorage
  useEffect(() => {
    const data = localStorage.getItem("allData");
    const user = localStorage.getItem("user");
    const month =
      localStorage.getItem("choosedMonth") || moment().format("MM-YYYY");
    if (data) {
      const parsed = JSON.parse(data);
      setAllData(parsed);
      setExpenses(parsed.myExpenses || []);
    }
    if (user) setUserData(JSON.parse(user));
    setChoosedMonth(month);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (choosedMonth) localStorage.setItem("choosedMonth", choosedMonth);
  }, [choosedMonth]);

  // Sync expenses when allData changes
  useEffect(() => {
    if (allData?.myExpenses) setExpenses(allData.myExpenses);
  }, [allData]);

  const fetchAllData = async () => {
    if (!userData) return;
    try {
      const response = await axios.post(`${MyDomain}Employee/GetAllData`, {
        UserId: userData.id,
      });
      const data = response.data || {};
      setAllData(data);
      setExpenses(data.myExpenses || []);
      localStorage.setItem("allData", JSON.stringify(data));
    } catch (err) {
      console.error("❌ Error fetching data:", err);
    }
  };

  if (loading || !allData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Month list ────────────────────────────────────────────────────────────
  const generateMonths = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const y = d.getFullYear();
      months.push(`${m}-${y}`);
    }
    return months;
  };
  const monthList = generateMonths();

  // ── Filter expenses ───────────────────────────────────────────────────────
  const filteredExpenses = expenses.filter((item) => {
    if (!choosedMonth || !item.date || item.date.trim() === "") return false;
    const parts = item.date.split("-");
    if (parts.length !== 3) return false;
    const [, month, year] = parts;
    return `${month}-${year}` === choosedMonth;
  });

  const applyInvoiceFilter = (list) => {
    if (invoiceFilter === "withInvoice")
      return list.filter((e) => e.withInvoice === 1);
    if (invoiceFilter === "withoutInvoice")
      return list.filter((e) => e.withInvoice !== 1);
    return list;
  };

  const finalFilteredExpenses = applyInvoiceFilter(filteredExpenses);

  const expensesWithoutDates = applyInvoiceFilter(
    expenses.filter((item) => !item.date || item.date.trim() === ""),
  );

  // ── Group by type ─────────────────────────────────────────────────────────
  const groupByType = () => {
    const grouped = {};
    expenseTypes.forEach((t) => {
      grouped[t.id] = { ...t, expenses: [], total: 0 };
    });
    finalFilteredExpenses.forEach((expense) => {
      if (grouped[expense.type]) {
        grouped[expense.type].expenses.push(expense);
        grouped[expense.type].total++;
      }
    });
    return grouped;
  };
  const groupedExpenses = groupByType();

  const totalExpenses = finalFilteredExpenses.length;
  const totalPrice = finalFilteredExpenses.reduce(
    (s, e) => s + (parseFloat(e.price) || 0),
    0,
  );

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getPlaceName = (placeId) => {
    if (!placeId) return "General";
    return (
      (allData.myPlaces || []).find((p) => p.id === placeId)?.place ||
      "Unknown Place"
    );
  };

  const getExpenseCreatorName = (creatorId) => {
    if (creatorId === userData?.id) return "Me";
    const emp = (allData.myEmployees || []).find((e) => e.id === creatorId);
    return emp?.name || "Unknown";
  };

  const getInvoiceForExpense = (expenseId) => {
    const expense = (allData.myExpenses || []).find((e) => e.id === expenseId);
    if (expense?.withInvoice !== 1) return null;
    const invoice = (allData.myInvoices || []).find(
      (inv) => inv.expensesId === expenseId,
    );
    return invoice || { pending: true, expensesId: expenseId };
  };

  const handleInvoiceButtonClick = (expense) => {
    const invoice = getInvoiceForExpense(expense.id);
    if (!invoice) return;
    if (invoice.pending) {
      alert("The invoice is being processed. Please refresh to view it.");
      fetchAllData();
    } else {
      setSelectedInvoice(invoice);
      setShowInvoiceModal(true);
    }
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const handleCreateSuccess = (newExpense) => {
    setExpenses([...expenses, newExpense]);
  };

  const handleUpdateSuccess = () => {
    fetchAllData();
    setShowUpdateModal(false);
    setSelectedExpense(null);
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm("Are you sure you want to delete this expense?"))
      return;
    try {
      await axios.delete(`${MyDomain}Expenses/DeleteExpense`, {
        data: { Id: expenseId },
      });
      fetchAllData();
      setExpenses(expenses.filter((e) => e.id !== expenseId));
    } catch (error) {
      console.error("❌ Error deleting expense:", error);
      alert("Failed to delete expense");
    }
  };

  // ── Selection ─────────────────────────────────────────────────────────────
  const toggleSelectionMode = () => {
    setSelectionMode((prev) => !prev);
    setSelectedExpenses([]);
  };

  const toggleExpenseSelection = (id) =>
    setSelectedExpenses((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const selectAllExpenses = () =>
    setSelectedExpenses(finalFilteredExpenses.map((e) => e.id));
  const deselectAllExpenses = () => setSelectedExpenses([]);

  // ── Export ────────────────────────────────────────────────────────────────
  const getDataToExport = () =>
    selectedExpenses.length > 0
      ? finalFilteredExpenses.filter((e) => selectedExpenses.includes(e.id))
      : finalFilteredExpenses;

  const exportToCSV = () => {
    setShowExportMenu(false);
    const data = getDataToExport();
    if (data.length === 0) {
      alert("No expenses to export");
      return;
    }

    const rows = [
      [
        "Date",
        "Title",
        "Place",
        "Price",
        "With Invoice",
        "Invoice Number",
        "Vendor Name",
        "Business Number",
        "Subtotal",
        "Tax Rate",
        "Tax Amount",
        "Total Amount",
        "Note",
      ],
      ...data.map((expense) => {
        const invoice = (allData.myInvoices || []).find(
          (inv) => inv.expensesId === expense.id,
        );
        return [
          invoice?.inoviceDate || expense.date || "",
          expense.title || "",
          getPlaceName(expense.placeId),
          expense.price || "0",
          expense.withInvoice === 1 ? "Yes" : "No",
          invoice?.inoviceNumber || "",
          invoice?.vendorName || "",
          invoice?.businessNumber || "",
          invoice?.subTotal || "",
          invoice?.taxRate ? `${invoice.taxRate}%` : "",
          invoice?.taxAmount || "",
          invoice?.totalAmount || "",
          expense.note || "",
        ];
      }),
    ];

    const csvContent =
      "data:text/csv;charset=utf-8," +
      rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");

    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", `expenses_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSelectionMode(false);
    setSelectedExpenses([]);
  };

  const exportToPDF = () => {
    setShowExportMenu(false);
    const data = getDataToExport();
    if (data.length === 0) {
      alert("No expenses to export");
      return;
    }

    const expensesWithInvoices = data.map((expense) => ({
      expense,
      invoice: (allData.myInvoices || []).find(
        (inv) => inv.expensesId === expense.id,
      ),
    }));

    const totalExpensePrice = data.reduce(
      (s, e) => s + (parseFloat(e.price) || 0),
      0,
    );
    const totalSubTotal = expensesWithInvoices.reduce(
      (s, { invoice }) => s + (parseFloat(invoice?.subTotal) || 0),
      0,
    );
    const totalTax = expensesWithInvoices.reduce(
      (s, { invoice }) => s + (parseFloat(invoice?.taxAmount) || 0),
      0,
    );
    const totalAmount = expensesWithInvoices.reduce(
      (s, { invoice }) => s + (parseFloat(invoice?.totalAmount) || 0),
      0,
    );

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
      body{font-family:Arial,sans-serif;padding:20px}
      h1{color:#F59E0B;text-align:center}
      .subtitle{text-align:center;color:#6B7280;margin-bottom:20px}
      .summary{background:#F9FAFB;padding:15px;border-radius:8px;margin-bottom:20px;border:1px solid #E5E7EB}
      .summary-row{display:flex;justify-content:space-between;padding:4px 0}
      table{width:100%;border-collapse:collapse;font-size:11px}
      th,td{border:1px solid #ddd;padding:8px;text-align:left}
      th{background:#F59E0B;color:#fff;font-weight:bold}
      .totals{background:#FEF3C7;font-weight:bold}
      .inv{background:#EFF6FF}
      .no-inv{background:#FEE2E2;color:#991B1B;text-align:center}
      .footer{margin-top:20px;text-align:center;color:#666;font-size:12px;border-top:2px solid #E5E7EB;padding-top:12px}
    </style></head><body>
      <h1>Expenses &amp; Invoices</h1>
      <div class="subtitle">Date: ${new Date().toLocaleDateString()}</div>
      <div class="summary">
        <div class="summary-row"><strong>Total Expenses:</strong><span>${data.length}</span></div>
        <div class="summary-row"><strong>With Invoice:</strong><span>${data.filter((e) => e.withInvoice === 1).length}</span></div>
        <div class="summary-row"><strong>Without Invoice:</strong><span>${data.filter((e) => e.withInvoice !== 1).length}</span></div>
      </div>
      <table><thead><tr>
        <th>Date</th><th>Title</th><th>Place</th><th>Price</th>
        <th>Invoice #</th><th>Vendor</th><th>Subtotal</th><th>Tax</th><th>Total</th>
      </tr></thead><tbody>
      ${expensesWithInvoices
        .map(
          ({ expense, invoice }) => `
        <tr ${invoice ? 'class="inv"' : ""}>
          <td>${invoice?.inoviceDate || expense.date || "-"}</td>
          <td>${expense.title || "-"}</td>
          <td>${getPlaceName(expense.placeId)}</td>
          <td>₪${parseFloat(expense.price || 0).toLocaleString()}</td>
          ${
            invoice
              ? `<td>${invoice.inoviceNumber || "-"}</td>
                 <td>${invoice.vendorName || "-"}</td>
                 <td>₪${parseFloat(invoice.subTotal || 0).toLocaleString()}</td>
                 <td>₪${parseFloat(invoice.taxAmount || 0).toLocaleString()} (${invoice.taxRate || 0}%)</td>
                 <td>₪${parseFloat(invoice.totalAmount || 0).toLocaleString()}</td>`
              : `<td class="no-inv" colspan="5">No Invoice</td>`
          }
        </tr>`,
        )
        .join("")}
      <tr class="totals">
        <td colspan="3"><strong>Totals</strong></td>
        <td><strong>₪${totalExpensePrice.toLocaleString()}</strong></td>
        <td colspan="2"></td>
        <td><strong>₪${totalSubTotal.toLocaleString()}</strong></td>
        <td><strong>₪${totalTax.toLocaleString()}</strong></td>
        <td><strong>₪${totalAmount.toLocaleString()}</strong></td>
      </tr>
      </tbody></table>
      <div class="footer">Generated on ${new Date().toLocaleString()}</div>
    </body></html>`;

    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    setTimeout(() => {
      win.print();
    }, 500);

    setSelectionMode(false);
    setSelectedExpenses([]);
  };

  // ── Chunk into pairs for 2-column grid ───────────────────────────────────
  const chunkArray = (arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size)
      chunks.push(arr.slice(i, i + size));
    return chunks;
  };

  // ── Expense card ──────────────────────────────────────────────────────────
  const renderExpenseCard = (expense, expenseType = null) => {
    const isSelected = selectedExpenses.includes(expense.id);
    const invoice = getInvoiceForExpense(expense.id);
    const type = expenseType || expenseTypes.find((t) => t.id === expense.type);

    return (
      <div
        key={expense.id}
        onClick={() => {
          if (selectionMode) toggleExpenseSelection(expense.id);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          if (!selectionMode) {
            setSelectionMode(true);
            toggleExpenseSelection(expense.id);
          }
        }}
        className={`relative rounded-xl p-3 border flex flex-col gap-2 cursor-pointer transition ${
          isSelected
            ? "bg-yellow-50 border-yellow-400 border-2"
            : "bg-gray-50 border-gray-200"
        }`}
        style={{
          flex: "1 1 calc(50% - 6px)",
          minWidth: 0,
          maxWidth: "calc(50% - 6px)",
        }}
      >
        {/* Selection checkbox */}
        {selectionMode && (
          <div className="absolute top-2 right-2 z-10">
            <div
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                isSelected
                  ? "bg-yellow-400 border-yellow-400"
                  : "bg-white border-gray-300"
              }`}
            >
              {isSelected && (
                <span className="text-white text-xs font-bold">✓</span>
              )}
            </div>
          </div>
        )}

        {/* Invoice button */}
        {invoice && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleInvoiceButtonClick(expense);
            }}
            className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-600 text-xs font-semibold px-2.5 py-1.5 rounded-lg self-start"
          >
            <FileText className="w-3.5 h-3.5" />
            {invoice.pending ? "Processing..." : "View Invoice"}
            {invoice.pending && <Clock className="w-3 h-3 text-yellow-500" />}
          </button>
        )}

        {/* Type icon */}
        {type && (
          <div className="flex items-center gap-1.5">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-sm"
              style={{ backgroundColor: `${type.color}20` }}
            >
              {type.icon}
            </div>
            <span className="text-xs font-semibold text-gray-500">
              {type.name}
            </span>
          </div>
        )}

        {/* Date */}
        {expense.date && expense.date.trim() !== "" ? (
          <div className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-500 font-medium">
              {moment(expense.date, "DD-MM-YYYY").format("MMM DD")}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5 text-gray-300" />
            <span className="text-xs text-gray-300">No date</span>
          </div>
        )}

        {/* Title */}
        <p className="text-sm font-semibold text-gray-900 line-clamp-2">
          {expense.title || "—"}
        </p>

        {/* Price badge */}
        {expense.price && (
          <div className="flex items-center gap-1.5 bg-red-50 px-2.5 py-1.5 rounded-lg self-start">
            <DollarSign className="w-3.5 h-3.5 text-red-500" />
            <span className="text-sm font-bold text-red-500">
              ₪{parseFloat(expense.price).toLocaleString()}
            </span>
          </div>
        )}

        {/* Place */}
        {expense.placeId && (
          <div className="flex items-center gap-1">
            <MapPin className="w-3 h-3 text-gray-400" />
            <span className="text-xs text-gray-400 truncate">
              {getPlaceName(expense.placeId)}
            </span>
          </div>
        )}

        {/* Note */}
        {expense.note && expense.note.trim() !== "" && (
          <p className="text-xs text-gray-500 italic line-clamp-2">
            {expense.note}
          </p>
        )}

        {/* Image thumbnail */}
        {expense.image && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedExpenseImage(expense.image);
              setImageModalVisible(true);
            }}
            className="relative rounded-lg overflow-hidden mt-1"
          >
            <img
              src={expense.image}
              alt="Expense"
              className="w-full h-24 object-cover rounded-lg"
            />
            <div className="absolute bottom-1.5 right-1.5 w-6 h-6 bg-black bg-opacity-60 rounded-full flex items-center justify-center">
              <Maximize2 className="w-3 h-3 text-white" />
            </div>
          </button>
        )}

        {/* Creator */}
        {expense.whoMake && (
          <div className="flex items-center gap-1 mt-1">
            <User className="w-3 h-3 text-gray-300" />
            <span className="text-[10px] text-gray-400 italic">
              by {getExpenseCreatorName(expense.whoMake)}
            </span>
          </div>
        )}

        {/* Edit / Delete */}
        {!selectionMode && (
          <div className="flex items-center gap-2 justify-end mt-1">
            {!invoice && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedExpense(expense);
                  setShowUpdateModal(true);
                }}
                className="p-1.5 hover:bg-blue-50 rounded-lg transition"
              >
                <Edit2 className="w-4 h-4 text-blue-500" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteExpense(expense.id);
              }}
              className="p-1.5 hover:bg-red-50 rounded-lg transition"
            >
              <Trash2 className="w-4 h-4 text-red-400" />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header ── */}
      <div className="bg-white px-5 py-4 rounded-b-2xl shadow-md">
        <div className="flex items-center gap-3">
          <button
            onClick={() => history.goBack()}
            className="w-9 h-9 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-11 h-11 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0">
              <Receipt className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">{t("totalExpenses")}</h1>
              <p className="text-xs text-gray-500">{t("expenseDetails")}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-5 py-5 space-y-5 pb-28">
        {/* ── Month Selector ── */}
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-3">{t("selectMonth")}</h2>
          <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
            {monthList.map((item) => {
              const [, year] = item.split("-");
              const monthName = moment(item, "MM-YYYY").format("MMM");
              const isSelected = choosedMonth === item;
              return (
                <button
                  key={item}
                  onClick={() => setChoosedMonth(item)}
                  className={`flex-shrink-0 px-5 py-3 rounded-2xl border-2 min-w-[84px] text-center transition-all ${
                    isSelected
                      ? "bg-yellow-400 border-yellow-400 shadow-lg"
                      : "bg-white border-gray-200 hover:border-yellow-300"
                  }`}
                >
                  <p
                    className={`text-sm font-semibold ${isSelected ? "text-white" : "text-gray-900"}`}
                  >
                    {monthName}
                  </p>
                  <p
                    className={`text-xs ${isSelected ? "text-yellow-100" : "text-gray-500"}`}
                  >
                    {year}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Summary Cards ── */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl p-4 shadow-sm border-l-4 border-yellow-400 flex flex-col items-center gap-2">
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <BarChart2 className="w-5 h-5 text-yellow-500" />
            </div>
            <p className="text-xs text-gray-500 font-medium">{t("totalExpenses")}</p>
            <p className="text-2xl font-bold text-gray-900">{totalExpenses}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border-l-4 border-red-400 flex flex-col items-center gap-2">
            <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-red-500" />
            </div>
            <p className="text-xs text-gray-500 font-medium">{t("totalAmount")}</p>
            <p className="text-2xl font-bold text-red-500">
              ₪{totalPrice.toLocaleString()}
            </p>
          </div>
        </div>

        {/* ── Invoice Filter ── */}
        <div className="flex gap-3 overflow-x-auto pb-1 hide-scrollbar">
          {[
            { value: "all", label: t("totalExpenses"), Icon: Layers },
            { value: "withInvoice", label: t("withInvoice"), Icon: FileText },
            {
              value: "withoutInvoice",
              label: t("withoutInvoice"),
              Icon: FileText,
            },
          ].map(({ value, label, Icon }) => (
            <button
              key={value}
              onClick={() => setInvoiceFilter(value)}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 font-semibold text-sm transition ${
                invoiceFilter === value
                  ? "bg-yellow-400 border-yellow-400 text-white shadow-md"
                  : "bg-white border-gray-200 text-gray-600 hover:border-yellow-300"
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* ── Selection + Export Bar ── */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Toggle selection mode */}
          <button
            onClick={toggleSelectionMode}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition ${
              selectionMode
                ? "bg-red-50 text-red-500"
                : "bg-yellow-100 text-yellow-600"
            }`}
          >
            {selectionMode ? (
              <X className="w-4 h-4" />
            ) : (
              <CheckSquare className="w-4 h-4" />
            )}
            {selectionMode ? t("cancel") : t("select")}
          </button>

          {selectionMode && (
            <>
              <button
                onClick={
                  selectedExpenses.length === finalFilteredExpenses.length
                    ? deselectAllExpenses
                    : selectAllExpenses
                }
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-yellow-100 text-yellow-600 font-semibold text-sm"
              >
                {selectedExpenses.length === finalFilteredExpenses.length ? (
                  <Square className="w-4 h-4" />
                ) : (
                  <CheckSquare className="w-4 h-4" />
                )}
                {selectedExpenses.length === finalFilteredExpenses.length
                  ? t("deselectAll")
                  : t("selectAll")}
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowExportMenu((p) => !p)}
                  disabled={selectedExpenses.length === 0}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-white transition ${
                    selectedExpenses.length === 0
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-yellow-400 hover:bg-yellow-500"
                  }`}
                >
                  <Download className="w-4 h-4" />
                  Export ({selectedExpenses.length})
                </button>
                {showExportMenu && selectedExpenses.length > 0 && (
                  <div className="absolute left-0 top-11 bg-white rounded-xl shadow-lg border border-gray-100 z-50 min-w-[140px] overflow-hidden">
                    <button
                      onClick={exportToCSV}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                    >
                      <FileText className="w-4 h-4 text-green-500" />
                      {t("export")} CSV
                    </button>
                    <div className="h-px bg-gray-100" />
                    <button
                      onClick={exportToPDF}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                    >
                      <FileText className="w-4 h-4 text-red-500" />
                      {t("export")} PDF
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {!selectionMode && (
            <div className="relative">
              <button
                onClick={() => setShowExportMenu((p) => !p)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-yellow-400 hover:bg-yellow-500 text-white font-semibold text-sm transition"
              >
                <Download className="w-4 h-4" />
                {t("exportAll")}
              </button>
              {showExportMenu && (
                <div className="absolute left-0 top-11 bg-white rounded-xl shadow-lg border border-gray-100 z-50 min-w-[140px] overflow-hidden">
                  <button
                    onClick={exportToCSV}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    <FileText className="w-4 h-4 text-green-500" />
                    {t("export")} CSV
                  </button>
                  <div className="h-px bg-gray-100" />
                  <button
                    onClick={exportToPDF}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    <FileText className="w-4 h-4 text-red-500" />
                    {t("export")} PDF
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Expenses by Category ── */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900">{t("byCategory")}</h2>
            <span className="bg-yellow-100 text-yellow-600 text-xs font-semibold px-3 py-1.5 rounded-xl">
              {Object.values(groupedExpenses).filter((g) => g.total > 0).length}{" "}
              categories
            </span>
          </div>

          {totalExpenses === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-5">
                <Receipt className="w-12 h-12 text-gray-300" />
              </div>
              <p className="text-lg font-semibold text-gray-500 mb-2">
                {t("noExpensesThisMonth")}
              </p>
              <p className="text-sm text-gray-400">
                Tap the + button to add an expense
              </p>
            </div>
          ) : (
            Object.values(groupedExpenses).map((group) => {
              if (group.total === 0) return null;
              return (
                <div
                  key={group.id}
                  className="bg-white rounded-2xl p-4 shadow-sm"
                >
                  {/* Category header */}
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-xl"
                      style={{ backgroundColor: group.bg }}
                    >
                      {group.icon}
                    </div>
                    <div>
                      <p className="text-base font-bold text-gray-900">
                        {group.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {group.total}{" "}
                        {group.total === 1 ? "expense" : "expenses"}
                      </p>
                    </div>
                  </div>

                  {/* 2-column grid */}
                  <div className="space-y-3">
                    {chunkArray(group.expenses, 2).map((pair, pIdx) => (
                      <div key={pIdx} className="flex gap-3">
                        {pair.map((expense) => renderExpenseCard(expense))}
                        {/* Spacer if odd number */}
                        {pair.length === 1 && (
                          <div
                            style={{
                              flex: "1 1 calc(50% - 6px)",
                              maxWidth: "calc(50% - 6px)",
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}

          {/* ── Expenses without dates ── */}
          {expensesWithoutDates.length > 0 && (
            <div className="bg-yellow-50 rounded-2xl p-4 border-2 border-yellow-200 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-5 h-5 text-yellow-500" />
                <h3 className="text-base font-bold text-yellow-800">
                  {t("expensesWithoutDate")} ({expensesWithoutDates.length})
                </h3>
              </div>
              <p className="text-xs text-yellow-700 mb-3">
                These expenses don't have a date assigned.
              </p>

              <div className="space-y-3">
                {chunkArray(expensesWithoutDates, 2).map((pair, pIdx) => (
                  <div key={pIdx} className="flex gap-3">
                    {pair.map((expense) => {
                      const type = expenseTypes.find(
                        (t) => t.id === expense.type,
                      );
                      return renderExpenseCard(expense, type);
                    })}
                    {pair.length === 1 && (
                      <div
                        style={{
                          flex: "1 1 calc(50% - 6px)",
                          maxWidth: "calc(50% - 6px)",
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── FAB ── */}
      <button
        onClick={() => setShowCreateModal(true)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-yellow-400 hover:bg-yellow-500 rounded-full flex items-center justify-center shadow-2xl transition z-40"
      >
        <Plus className="w-8 h-8 text-white" />
      </button>

      {/* ── Modals ── */}
      <BossCreateExpenses
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      <BossUpdateExpensesModal
        visible={showUpdateModal}
        onClose={() => {
          setShowUpdateModal(false);
          setSelectedExpense(null);
        }}
        expense={selectedExpense}
        onUpdateSuccess={handleUpdateSuccess}
      />

      <BossModalShowInvoice
        visible={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
      />

      <BossStatisticByDateModalShowInvoices
        visible={showAllInvoicesModal}
        onClose={() => setShowAllInvoicesModal(false)}
      />

      {/* ── Image preview modal ── */}
      {imageModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center">
          <button
            onClick={() => setImageModalVisible(false)}
            className="absolute top-10 right-5 z-10"
          >
            <X className="w-9 h-9 text-white" />
          </button>
          {selectedExpenseImage && (
            <img
              src={selectedExpenseImage}
              alt="Expense"
              className="max-w-full max-h-[80vh] object-contain"
            />
          )}
        </div>
      )}

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}
